/**
 * TV pairing state machine (docs/04 §3 TV-1, docs/03 §2 "TV pairing"). Framework-free so it
 * can be unit-tested with fake timers; `Pairing.tsx` binds it to the store and the view.
 *
 *   idle ─start()─▶ starting ─▶ pending ─(RTDB approved | poll approved)─▶ approved ─▶ done
 *                     │            │  └─(denied)─▶ denied ─2.5 s─▶ starting
 *                     │            └─(expired node | countdown 0)─▶ expired ─▶ starting
 *                     ├─(403 device_blocked)─▶ blocked ─[New code]─▶ starting
 *                     └─(error / 429)─▶ error ─backoff─▶ starting
 *
 * Watching: with Firebase the controller subscribes to `/pairing/{deviceCode}` (`watch`);
 * without it (offline-auth dev mode) it polls `POST /tv/pair/exchange` every 3 s. The
 * exchange is one-shot on the API, so it is only issued once per code. A pending code is
 * persisted so an app restart inside the 10-minute window shows the same code again
 * (checked with one exchange call, which answers `pending` without consuming it).
 */
import {
  type ApiClient,
  isAkpError,
  type PairExchangeApproved,
  type PairingNode,
  type PairStartResponse,
  type Platform,
} from '@akpl/shared';

export const PAIRING_STORAGE_KEY = 'akp.pairing';
export const POLL_INTERVAL_MS = 3000;
export const DENIED_DELAY_MS = 2500;
/** A stored code with less than this left is not worth resuming. */
export const RESUME_MIN_LEFT_MS = 15_000;
export const RETRY_BACKOFF_MS = [3000, 6000, 12_000, 30_000] as const;

export type PairingPhase =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'pending'; code: PairStartResponse; expiresAt: number }
  | { kind: 'approved'; code: PairStartResponse }
  | { kind: 'denied'; code: PairStartResponse }
  | { kind: 'expired'; code: PairStartResponse }
  | { kind: 'blocked' }
  | { kind: 'error'; message: string; retryAt: number }
  | { kind: 'done'; sid: string };

export interface PairingState {
  phase: PairingPhase;
  /** Clock used by the countdown; advanced once per second while pending. */
  now: number;
}

export interface PairingDevice {
  id: string;
  platform: Platform;
  model?: string;
  appVersion?: string;
}

export type Unsubscribe = () => void;

export interface PairingDeps {
  /** `pairResume` is optional so a host without it (older tests, fixtures) just shows the code. */
  api: Pick<ApiClient, 'pairStart' | 'pairExchange'> & Partial<Pick<ApiClient, 'pairResume'>>;
  device: PairingDevice;
  /** RTDB subscription to `/pairing/{deviceCode}`; return `null` to fall back to polling. */
  watch: (deviceCode: string, cb: (node: PairingNode | null) => void) => Unsubscribe | null;
  /** `signInWithCustomToken`; `null` in offline-auth mode (the session is stored without a token). */
  signIn: ((customToken: string) => Promise<{ uid: string }>) | null;
  onPaired: (result: PairExchangeApproved, uid: string) => void;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  log?: (level: 'warn' | 'error', message: string) => void;
  now?: () => number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
  pollIntervalMs?: number;
  deniedDelayMs?: number;
}

interface StoredPairing {
  deviceId: string;
  code: PairStartResponse;
}

function readStored(
  storage: PairingDeps['storage'],
  deviceId: string,
  now: number,
): PairStartResponse | null {
  try {
    const raw = storage?.getItem(PAIRING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPairing>;
    const code = parsed.code;
    if (parsed.deviceId !== deviceId || !code || typeof code.deviceCode !== 'string') return null;
    if (Date.parse(code.expiresAt) - now < RESUME_MIN_LEFT_MS) return null;
    return code;
  } catch {
    return null;
  }
}

export class PairingController {
  private state: PairingState;
  private readonly listeners = new Set<() => void>();
  private unwatch: Unsubscribe | null = null;
  private pollTimer: unknown = null;
  private tickTimer: unknown = null;
  private retryTimer: unknown = null;
  private exchanging = false;
  private failures = 0;
  private stopped = false;
  /** Bumped on every teardown so late async results of a previous code are ignored. */
  private generation = 0;

  constructor(private readonly deps: PairingDeps) {
    this.state = { phase: { kind: 'idle' }, now: this.now() };
  }

  getState = (): PairingState => this.state;

  subscribe = (listener: () => void): Unsubscribe => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * A TV paired before first asks to reopen its host's room (`POST /tv/pair/resume`): when its
   * last session only ran out of time it comes back without the QR. Otherwise it resumes a
   * persisted code when that is still live, or requests a new one.
   */
  async start(): Promise<void> {
    this.stopped = false;
    if (this.deps.api.pairResume && (await this.resumeDevice())) return;
    if (this.stopped) return;
    const stored = readStored(this.deps.storage, this.deps.device.id, this.now());
    if (!stored) {
      await this.requestCode();
      return;
    }
    const gen = this.generation;
    this.setPhase({ kind: 'starting' });
    try {
      const res = await this.deps.api.pairExchange({ body: { deviceCode: stored.deviceCode } });
      if (gen !== this.generation || this.stopped) return;
      if (res.status === 'pending') {
        this.setPending(stored);
        return;
      }
      if (res.status === 'approved') {
        this.setPhase({ kind: 'approved', code: stored });
        await this.finish(res, gen);
        return;
      }
    } catch (err) {
      this.deps.log?.('warn', `pairing resume failed: ${String(err)}`);
    }
    if (gen !== this.generation || this.stopped) return;
    this.clearStored();
    await this.requestCode();
  }

  /** True when the resume answered with a session (`finish` owns what happens next). */
  private async resumeDevice(): Promise<boolean> {
    const resume = this.deps.api.pairResume;
    if (!resume) return false;
    const gen = this.generation;
    this.setPhase({ kind: 'starting' });
    try {
      const res = await resume({ body: { deviceId: this.deps.device.id } });
      if (gen !== this.generation || this.stopped || res.status !== 'approved') return false;
      // `finish` falls back to a new code itself when the sign-in fails.
      await this.finish(res, gen);
      return true;
    } catch (err) {
      this.deps.log?.('warn', `pair/resume failed: ${String(err)}`);
      return false;
    }
  }

  /** "Generate new code" button: drops the current code and asks for another. */
  async newCode(): Promise<void> {
    this.teardown();
    await this.requestCode();
  }

  /** Unmount: stop timers and subscriptions; the persisted code survives for a resume. */
  stop(): void {
    this.stopped = true;
    this.teardown();
  }

  // --- transitions ------------------------------------------------------------------

  private async requestCode(): Promise<void> {
    if (this.stopped) return;
    const gen = this.generation;
    this.setPhase({ kind: 'starting' });
    const { device } = this.deps;
    try {
      const code = await this.deps.api.pairStart({
        body: {
          deviceId: device.id,
          platform: device.platform,
          ...(device.model ? { model: device.model } : {}),
          ...(device.appVersion ? { appVersion: device.appVersion } : {}),
        },
      });
      if (gen !== this.generation || this.stopped) return;
      this.failures = 0;
      this.setPending(code);
    } catch (err) {
      if (gen !== this.generation || this.stopped) return;
      if (isAkpError(err, 'device_blocked')) {
        this.clearStored();
        this.setPhase({ kind: 'blocked' });
        return;
      }
      const retryAfterSec = isAkpError(err, 'rate_limited')
        ? Number(err.details?.retryAfterSec)
        : NaN;
      const delay =
        Number.isFinite(retryAfterSec) && retryAfterSec > 0
          ? retryAfterSec * 1000
          : RETRY_BACKOFF_MS[Math.min(this.failures, RETRY_BACKOFF_MS.length - 1)]!;
      this.failures += 1;
      const message = err instanceof Error ? err.message : String(err);
      this.deps.log?.('warn', `pair/start failed: ${message}`);
      this.setPhase({ kind: 'error', message, retryAt: this.now() + delay });
      this.retryTimer = this.timeout(() => void this.requestCode(), delay);
    }
  }

  private setPending(code: PairStartResponse): void {
    const expiresAt = Date.parse(code.expiresAt);
    this.persist(code);
    this.exchanging = false;
    this.setPhase({ kind: 'pending', code, expiresAt });
    this.scheduleTick();
    this.unwatch = this.deps.watch(code.deviceCode, (node) => this.onNode(node));
    if (!this.unwatch) this.schedulePoll();
  }

  private onNode(node: PairingNode | null): void {
    if (this.state.phase.kind !== 'pending' && this.state.phase.kind !== 'approved') return;
    if (!node) return; // not written yet (or cleaned up long after expiry)
    switch (node.status) {
      case 'approved':
        void this.exchange();
        break;
      case 'denied':
        this.onDenied();
        break;
      case 'expired':
        this.onExpired();
        break;
      case 'pending':
        break;
    }
  }

  /** One-shot exchange (RTDB said approved, or a poll tick in offline mode). */
  private async exchange(): Promise<void> {
    const phase = this.state.phase;
    if (phase.kind !== 'pending' && phase.kind !== 'approved') return;
    if (this.exchanging) return;
    this.exchanging = true;
    const gen = this.generation;
    const { code } = phase;
    try {
      const res = await this.deps.api.pairExchange({ body: { deviceCode: code.deviceCode } });
      if (gen !== this.generation || this.stopped) return;
      switch (res.status) {
        case 'approved':
          this.setPhase({ kind: 'approved', code });
          await this.finish(res, gen);
          return;
        case 'pending':
          this.exchanging = false; // poll again later
          return;
        case 'denied':
          this.onDenied();
          return;
        case 'expired':
          this.onExpired();
          return;
      }
    } catch (err) {
      if (gen !== this.generation || this.stopped) return;
      this.deps.log?.('warn', `pair/exchange failed: ${String(err)}`);
      if (isAkpError(err) && err.status < 500 && err.status !== 429 && err.status !== 0) {
        // 404 unknown, 409 consumed, 410 expired: this code is dead → get a new one.
        this.teardown();
        await this.requestCode();
        return;
      }
      // Transient (network / 5xx / 429): retry on a poll timer. In RTDB mode the node will
      // not change again after `approved`, so polling is the only way to try again.
      this.exchanging = false;
      if (!this.pollTimer) this.schedulePoll();
    }
  }

  private async finish(res: PairExchangeApproved, gen: number): Promise<void> {
    try {
      const uid = this.deps.signIn
        ? (await this.deps.signIn(res.customToken)).uid
        : `tv:${this.deps.device.id}`;
      if (gen !== this.generation || this.stopped) return;
      this.clearStored();
      this.teardown();
      this.setPhase({ kind: 'done', sid: res.session.id });
      this.deps.onPaired(res, uid);
    } catch (err) {
      if (gen !== this.generation || this.stopped) return;
      this.deps.log?.('error', `sign-in with the TV token failed: ${String(err)}`);
      this.teardown();
      await this.requestCode();
    }
  }

  private onDenied(): void {
    const phase = this.state.phase;
    if (phase.kind !== 'pending' && phase.kind !== 'approved') return;
    this.teardown();
    this.setPhase({ kind: 'denied', code: phase.code });
    this.retryTimer = this.timeout(
      () => void this.requestCode(),
      this.deps.deniedDelayMs ?? DENIED_DELAY_MS,
    );
  }

  private onExpired(): void {
    const phase = this.state.phase;
    if (phase.kind !== 'pending' && phase.kind !== 'approved') return;
    this.teardown();
    this.setPhase({ kind: 'expired', code: phase.code });
    void this.requestCode();
  }

  // --- timers --------------------------------------------------------------------------

  private scheduleTick(): void {
    this.tickTimer = this.timeout(() => {
      this.tickTimer = null;
      const phase = this.state.phase;
      if (phase.kind !== 'pending') return;
      const now = this.now();
      if (now >= phase.expiresAt) {
        this.onExpired();
        return;
      }
      this.setState({ ...this.state, now });
      this.scheduleTick();
    }, 1000);
  }

  private schedulePoll(): void {
    this.pollTimer = this.timeout(() => {
      this.pollTimer = null;
      const { kind } = this.state.phase;
      if (kind !== 'pending' && kind !== 'approved') return;
      void this.exchange().finally(() => {
        const { kind: after } = this.state.phase;
        if ((after === 'pending' || after === 'approved') && !this.pollTimer) this.schedulePoll();
      });
    }, this.deps.pollIntervalMs ?? POLL_INTERVAL_MS);
  }

  private teardown(): void {
    this.generation += 1;
    this.exchanging = false;
    this.unwatch?.();
    this.unwatch = null;
    for (const key of ['pollTimer', 'tickTimer', 'retryTimer'] as const) {
      const id = this[key];
      if (id !== null) this.clear(id);
      this[key] = null;
    }
  }

  // --- helpers ---------------------------------------------------------------------------

  private persist(code: PairStartResponse): void {
    try {
      const value: StoredPairing = { deviceId: this.deps.device.id, code };
      this.deps.storage?.setItem(PAIRING_STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* private mode / quota */
    }
  }

  private clearStored(): void {
    try {
      this.deps.storage?.removeItem(PAIRING_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }

  private timeout(fn: () => void, ms: number): unknown {
    return (this.deps.setTimeout ?? ((f, m) => globalThis.setTimeout(f, m)))(fn, ms);
  }

  private clear(id: unknown): void {
    (this.deps.clearTimeout ?? ((i) => globalThis.clearTimeout(i as number)))(id);
  }

  private setPhase(phase: PairingPhase): void {
    this.setState({ phase, now: this.now() });
  }

  private setState(next: PairingState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

/** "mm:ss" left, never negative. */
export function formatCountdown(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** `https://acapella.lol/tv` → `acapella.lol/tv` for the on-screen text. */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}
